import { memo, ReactElement, useCallback, useMemo, useState, useEffect } from "react"
import styled from "styled-components"
import { statusLabelsFilters } from "constants/invoice-table"
import { columns } from "constants/invoice-table"
import ApiInvoices from "api/ApiInvoices"
import { useNavigate } from "react-router-dom"
import DatePicker from "components/EvtKit/date-picker/DatePicker"
import Heading from "components/Typography/Heading"
import { BsArrowClockwise } from "react-icons/bs"
import Caption from "components/Typography/Caption"
import { AppState, useAppSelector } from "reducers/store"
import { useQuery } from "hooks/useQuery"
import { useDebouncedCallback } from "use-debounce"
import { useQueryState } from "context/queryState"
import { INVOICE_FETCH } from "reducers/invoice/invoice-types"
import { BeatTable } from "components/Beat/BeatTable/layout"
import Input from "components/EvtKit/input/Input"
import { SET_PRODUCT_LIST } from "reducers/products/actions-types"
import ApiProducts from "api/ApiProducts"
import SelectV2, { Option } from "components/EvtKit/select/SelectV2"
import { productsMapperSelect } from "utils/mapper/event-types"
import { useDeepMemo } from "utils/useDeepMemo"

interface ContainerProps {
  direction?: string
  justify?: string
  align?: string
  mb?: string
}

const Container = styled.div<ContainerProps>`
  width: 100%;
  height: 100%;
  background: ${(props) => props.theme.palette.gray_ice};
`

export const Title = styled.span`
  display: flex;
  align-items: center;
  padding: 48px 0px 48px 0px;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 24px 24px 0px;
`
const ContainerGeneralFilters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ContainerTitleClearFilters = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const ClearFiltersContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  flex-direction: row;
  cursor: pointer;
  gap: 4px;
  padding: 8px 20px 8px 20px;
  border-radius: 40px;
  border: 1px solid;

  color: ${(props) => props.theme.palette.primary_one};

  &.disabled {
    color: ${(props) => props.theme.palette.disabled};
    cursor: not-allowed;
  }
`

const ClearFiltersIconContainer = styled.div`
  font-size: 20px;
  transform: rotate(45deg);
  color: ${(props) => props.theme.palette.primary_one};

  &.disabled {
    color: ${(props) => props.theme.palette.disabled};
  }
`

const ContainerFilters = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  background-color: ${(props) => props.theme.palette.white};
  padding: 24px 24px 16px 24px;
  border-radius: 10px;
  box-shadow: 0px 10px 56px rgba(0, 0, 0, 0.05);
  align-items: center;
  margin-bottom: 24px;
`

const InvoiceFilterSelectContainer = styled.div`
  max-width: 240px;
  width: 100%;
  display: flex;
`

const StyledInput = styled(Input)`
  width: 100%;
  max-width: 240px;

  label {
    width: fit-content;
  }
`

const TableSection = styled.div``

const initialDebouncedFilters: App.InvoiceParams = {
  cnpj: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined
}

const initialFilters: App.InvoiceParams = {
  ...initialDebouncedFilters,
  productName: undefined,
  status: undefined
}
const InvoiceTable = (): ReactElement => {
  const navigate = useNavigate()
  const queryParams = useQuery()

  const { listInvoices } = useAppSelector((x: AppState) => x.invoices)

  const [filters, setFilters] = useState<App.InvoiceParams>(initialFilters)
  const [debouncedFilters, setDebouncedFilters] = useState<App.InvoiceParams>(initialDebouncedFilters)
  const [skip, setSkip] = useState<string>(queryParams.get("skip") ?? "0")
  const [take, setTake] = useState<string>(queryParams.get("take") ?? "10")

  const memoizedFilters = useDeepMemo(() => filters, [filters])

  const searchFilters = {
    cnpj: filters.cnpj,
    createdAtStart: filters.createdAtStart,
    createdAtEnd: filters.createdAtEnd,
    productName: filters.productName,
    status: filters.status,
    skip: skip,
    take: take
  }

  const debouncedFiltersCallback = useDebouncedCallback(() => {
    setFilters((state) => ({
      ...state,
      ...debouncedFilters
    }))
  }, 1000)

  useEffect(() => {
    debouncedFiltersCallback()
  }, [debouncedFilters, debouncedFiltersCallback])

  useQueryState(
    [INVOICE_FETCH, searchFilters],
    async () =>
      new ApiInvoices().searchInvoices({
        ...filters,
        skip: Number(skip),
        take: Number(take)
      }),
    INVOICE_FETCH
  )

  useQueryState([SET_PRODUCT_LIST], async () => (await new ApiProducts().getAll()).products, SET_PRODUCT_LIST, {
    refetchOnMount: "always"
  })

  const { productList } = useAppSelector((state) => state.product)

  const handleFiltersChange = useCallback((key: string, value: string) => {
    setFilters((prevState) => ({
      ...prevState,
      [key]: value || undefined
    }))
  }, [])

  const handleStatusChange = useCallback((option: Option | null) => {
    handleFiltersChange("status", option?.value || "")
  }, [])

  const handleProductChange = useCallback((option: Option | null) => {
    handleFiltersChange("productName", option?.value || "")
  }, [])

  const handleInputChange = useCallback((e) => {
    setDebouncedFilters((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value || undefined
    }))
  }, [])

  const handleDateFilterChange = useCallback((date: [Date | null, Date | null]) => {
    setDebouncedFilters((prevState) => ({
      ...prevState,
      createdAtStart: date[0] || undefined,
      createdAtEnd: date[1] || undefined
    }))
  }, [])

  const clearFilters = useCallback(() => {
    setFilters(initialFilters)
    setDebouncedFilters(initialDebouncedFilters)
  }, [])

  const handleNavigate = useCallback(
    (params) => {
      const queries = new URLSearchParams(params.toString().split("?")[1])

      const skip = parseInt(queries.get("skip") ?? "0")
      const take = parseInt(queries.get("take") ?? "10")

      setSkip(skip.toString())
      setTake(take.toString())

      return navigate
    },
    [navigate]
  )

  const enableClearFilter = useMemo(() => Object.values(filters).some((item) => !!item), [filters])

  const getDataToProducts = useCallback(async () => {
    const prodOptions = productsMapperSelect(productList.data)

    return {
      hasMore: false,
      results: prodOptions
    }
  }, [productList])

  return (
    <Container direction="column" align="center">
      <Title>
        <Heading variant="h3">Faturas</Heading>
      </Title>
      <Content>
        <ContainerGeneralFilters>
          <ContainerTitleClearFilters>
            <Heading variant="h5" color="gray-dark">
              Filtrar
            </Heading>
            {enableClearFilter && (
              <ClearFiltersContainer onClick={clearFilters}>
                <ClearFiltersIconContainer>
                  <BsArrowClockwise />
                </ClearFiltersIconContainer>
                <Caption size="large" color="primary-dark" weight="semi-bold">
                  Limpar Filtros
                </Caption>
              </ClearFiltersContainer>
            )}

            {!enableClearFilter && (
              <ClearFiltersContainer className="disabled">
                <ClearFiltersIconContainer className="disabled">
                  <BsArrowClockwise />
                </ClearFiltersIconContainer>
                <Caption size="large" color="gray-light" weight="semi-bold">
                  Limpar Filtros
                </Caption>
              </ClearFiltersContainer>
            )}
          </ContainerTitleClearFilters>
          <ContainerFilters>
            <InvoiceFilterSelectContainer>
              <SelectV2
                id="product"
                label="Filtrar por produto"
                onChange={handleProductChange}
                placeholder="Todos"
                shrink
                getData={getDataToProducts}
                value={filters.productName || ""}
                className="fullWidth"
              />
            </InvoiceFilterSelectContainer>
            <StyledInput
              label="Filtrar pelo CNPJ"
              name="cnpj"
              onChange={handleInputChange}
              placeholder="Digite o CNPJ"
              shrink
              value={debouncedFilters.cnpj || ""}
            />
            <DatePicker
              dateFormat="dd/MM/yyyy"
              onChange={(dates) => handleDateFilterChange(dates as unknown as [Date | null, Date | null])}
              startDate={debouncedFilters.createdAtStart}
              endDate={debouncedFilters.createdAtEnd}
              selectsRange
              placeholderText="Selecione (De - Até)"
              label="Data da fatura"
              shrink
              id="createdAt"
            />
            <InvoiceFilterSelectContainer>
              <SelectV2
                id="status"
                label="Filtrar por status"
                onChange={handleStatusChange}
                placeholder="Todos"
                shrink
                value={filters.status || ""}
                className="fullWidth"
                options={statusLabelsFilters}
              />
            </InvoiceFilterSelectContainer>
          </ContainerFilters>
        </ContainerGeneralFilters>
        <TableSection>
          <BeatTable
            columnsjson={columns}
            data={listInvoices.invoices}
            loading={listInvoices.loading}
            totalItems={listInvoices.totalItems}
            initialPage={Number(skip) / Number(take)}
            initialTake={Number(take)}
            navigate={handleNavigate}
            applyParamsToQuery
            filters={memoizedFilters}
          />
        </TableSection>
      </Content>
    </Container>
  )
}

export default memo(InvoiceTable)
